import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SnResizable from "@shared/components/SnResizable";
import SnGrid from "@shared/components/GridV2/SnGrid";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import { global } from "@shared/libs/Global";
import { getInitPopUpData, normalizeKey, toNum } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import { menuInfo } from "@shared/type/CommonType";
import { getFooterFieldValue } from "./WP_POPUP_COMMON";

interface PopupProps {
  selectedData?: any;
  modalRef?: any;
  Menuprops?: menuInfo;
}

const PRD_MANUAL_CONS_POP_01 = forwardRef(
  ({ selectedData, Menuprops }: PopupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const popupRootRef = useRef<HTMLDivElement>(null);
    const stockBarcodeRef = useRef<any>(null);
    const inventoryRowsRef = useRef<any[]>([]);
    const popupReturnRowsRef = useRef<any[]>([]);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
      grid3: React.createRef(),
    });

    const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(
      null
    );
    // 컬럼/데이터는 DB 메타 기반으로만 구성
    const [columns, setColumns] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });

    // 탭별 그리드 리사이즈
    // 최초 1회는 기본 사이즈로 시작하고, 이후 사용자 조정값을 이 키에 저장해 재사용한다.
    const resizeMenuKey = `${
      Menuprops?.cd_menu || "PRD_MANUAL_CONS_POP_01"
    }_TAB_INIT_V1`;
    const defaultSize = useMemo(
      () => ({
        tab1Grid: "32%",
        tab2Grid: "32%",
        tab3Grid: "32%",
      }),
      []
    );
    const [sizes, onResizeStop] = usePanelSizes(resizeMenuKey, defaultSize);

    // 재고 resultset(data[1]) 중 QT 제외 항목만 grid2에 바인딩한다.
    const filterNonQtRows = (rows: any[]) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.filter(
        (row: any) => (row?.fg_unit || "").toString() !== "QT"
      );
    };

    const focusStockBarcodeInput = () => {
      stockBarcodeRef.current?.focus?.();
    };

    // grid3 footer의 투입수량 합계를 grid1 선택행 실투입수량(qt)에 동기화

    const syncGrid3FooterToGrid1SelectedQt = async () => {
      const sumQty =
        Number(await getFooterFieldValue(gridRefs.current.grid3, "qt")) || 0;
      const focusedNode = gridRefs.current.grid1.current?.focusedRowNode?.();
      if (!focusedNode?.data) return;
      focusedNode.setDataValue?.("qt", sumQty, "noEvent");
    };

    const findGrid2RowByBarcode = (barcodeValue: string) => {
      const scan = normalizeKey(barcodeValue);
      if (!scan) return { row: null, rowIndex: -1 };
      let matched: any = null;
      let rowIndex = -1;
      gridRefs.current.grid2.current?.forEachNode?.((node: any) => {
        if (matched || !node?.data) return;
        const row = node.data;
        const noBarcode = normalizeKey(row?.no_barcode);
        if (scan === noBarcode) {
          matched = row;
          rowIndex = node.rowIndex ?? -1;
        }
      });
      return { row: matched, rowIndex };
    };

    const rowKeyByMatlBarcode = (row: any) =>
      `${normalizeKey(row?.cd_matl)}|${normalizeKey(row?.no_barcode)}`;

    const findGrid3RowByMatlBarcode = (row: any) => {
      const keyCdMatl = normalizeKey(row?.cd_matl);
      const keyBarcode = normalizeKey(row?.no_barcode);
      let foundIndex = -1;
      let foundNode: any = null;
      gridRefs.current.grid3.current?.forEachNode?.((node: any) => {
        if (foundNode || !node?.data) return;
        const d = node.data;
        if (
          normalizeKey(d?.cd_matl) === keyCdMatl &&
          normalizeKey(d?.no_barcode) === keyBarcode
        ) {
          foundIndex = node.rowIndex ?? -1;
          foundNode = node;
        }
      });
      return { foundIndex, foundNode };
    };

    const mergeQtyToGrid3Row = (node: any, addQty: number) => {
      node?.setDataValue?.("qt", toNum(node?.data?.qt) + addQty, "noEvent");
    };

    const addNewGrid3Row = (row: any, qty: number) => {
      const addedNode = gridRefs.current.grid3.current?.addRow?.({
        ...row,
        qt: qty,
      });
      const rowIndex = addedNode?.rowIndex;
      if (rowIndex > -1) {
        gridRefs.current.grid3.current?.selectAndFocusRow?.(rowIndex);
        return;
      }
      const rowCount =
        gridRefs.current.grid3.current?.getDisplayedRowCount?.() || 0;
      if (rowCount > 0) {
        gridRefs.current.grid3.current?.selectAndFocusRow?.(rowCount - 1);
      }
    };

    const recalcGrid2QtyUseRemain = () => {
      const baseRemainByKey: Record<string, number> = {};
      (inventoryRowsRef.current || []).forEach((row: any) => {
        const key = rowKeyByMatlBarcode(row);
        baseRemainByKey[key] = toNum(baseRemainByKey[key]) + toNum(row?.qt_remain);
      });

      const usedByKey: Record<string, number> = {};
      gridRefs.current.grid3.current?.forEachNode?.((node: any) => {
        if (!node?.data) return;
        const key = rowKeyByMatlBarcode(node.data);
        usedByKey[key] = toNum(usedByKey[key]) + toNum(node.data?.qt);
      });

      gridRefs.current.grid2.current?.forEachNode?.((node: any) => {
        if (!node?.data) return;
        const key = rowKeyByMatlBarcode(node.data);
        const baseRemain = toNum(baseRemainByKey[key]);
        const usedQty = toNum(usedByKey[key]);
        const nextRemain = Math.max(0, baseRemain - usedQty);
        node.setDataValue?.("qt_use", 0, "noEvent");
        node.setDataValue?.("qt_remain", nextRemain, "noEvent");
      });
    };

    const appendRowToGrid3 = async (row: any, mode: "scan" | "manual") => {
      if (!row) return;
      const selectedGrid1 =
        gridRefs.current.grid1.current?.getSelectedRows?.()?.[0];
      const focusedGrid1 =
        gridRefs.current.grid1.current?.focusedRowNode?.()?.data;
      const planQty = toNum(selectedGrid1?.qt_plan ?? focusedGrid1?.qt_plan);
      const processedQty = toNum(
        await getFooterFieldValue(gridRefs.current.grid3, "qt")
      );
      const remainNeed = Math.max(0, planQty - processedQty);
      const remainStock = toNum(row?.qt_remain);
      const requestQty = mode === "scan" ? remainNeed : toNum(row?.qt_use);
      if (mode === "manual" && requestQty <= 0) {
        await showDialog("alter", "적용수량을 확인해주세요.");
        focusStockBarcodeInput();
        return;
      }
      if (mode === "scan" && remainNeed <= 0) {
        await showDialog("alter", "투입 잔량이 없습니다.");
        focusStockBarcodeInput();
        return;
      }
      if (remainStock <= 0) {
        await showDialog("alter", "현재고가 없습니다.");
        focusStockBarcodeInput();
        return;
      }
      const applyQty =
        mode === "scan"
          ? Math.min(remainNeed, remainStock, requestQty)
          : Math.min(remainStock, requestQty);

      if (applyQty <= 0) {
        if (mode === "manual") {
          await showDialog("alter", "적용수량을 확인해주세요.");
        }
        focusStockBarcodeInput();
        return;
      }

      const { foundIndex, foundNode } = findGrid3RowByMatlBarcode(row);
      if (foundNode) {
        mergeQtyToGrid3Row(foundNode, applyQty);
        gridRefs.current.grid3.current?.selectAndFocusRow?.(foundIndex);
        recalcGrid2QtyUseRemain();
        void syncGrid3FooterToGrid1SelectedQt();
        focusStockBarcodeInput();
        return;
      }

      addNewGrid3Row(row, applyQty);
      recalcGrid2QtyUseRemain();
      void syncGrid3FooterToGrid1SelectedQt();
    };

    const moveGrid2RowToGrid3 = async (
      row: any,
      rowIndex = -1,
      mode: "scan" | "manual" = "manual"
    ) => {
      if (!row) return;
      if (rowIndex > -1) {
        gridRefs.current.grid2.current?.selectAndFocusRow?.(rowIndex);
      }
      await appendRowToGrid3(row, mode);
      focusStockBarcodeInput();
    };

    // 바코드 스캔 처리: grid2 대상행 선택 후 grid3에 추가하고 포커스를 복귀한다.
    const onStockBarcodeEnter = async (value: any) => {
      const scan = normalizeKey(value);
      if (!scan) {
        focusStockBarcodeInput();
        return;
      }

      const found = findGrid2RowByBarcode(scan);
      if (!found.row) {
        await showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
        focusStockBarcodeInput();
        return;
      }

      await moveGrid2RowToGrid3(found.row, found.rowIndex, "scan");
    };

    // 확인 시 처리내역(grid3) 전체 행만 반환
    const getGrid3Rows = () => {
      const rows: any[] = [];
      gridRefs.current.grid3.current?.forEachNode?.((node: any) => {
        if (node?.data) rows.push(node.data);
      });
      return rows;
    };

    const getGrid1Rows = () => {
      const rows: any[] = [];
      gridRefs.current.grid1.current?.forEachNode?.((node: any) => {
        if (node?.data) rows.push(node.data);
      });
      return rows;
    };

    const buildPopupReturnRows = async () => {
      const inventoryRows = Array.isArray(inventoryRowsRef.current)
        ? inventoryRowsRef.current
        : [];
      const grid1Rows = getGrid1Rows();
      const grid3Rows = getGrid3Rows();
      const usedQtyByKey: Record<string, number> = {};
      const resultRows: any[] = [];

      const getInvKey = (row: any) =>
        [
          normalizeKey(row?.cd_matl),
          normalizeKey(row?.no_io),
          toNum(row?.no_line_io),
          toNum(row?.no_line_lot_io),
          normalizeKey(row?.no_barcode),
        ].join("|");

      const getRemain = (row: any) =>
        toNum(row?.qt_remain) - toNum(usedQtyByKey[getInvKey(row)]);

      const consumeQty = (row: any, qty: number) => {
        const key = getInvKey(row);
        usedQtyByKey[key] = toNum(usedQtyByKey[key]) + qty;
      };

      // QT: grid1.qt 기준 FIFO 차감
      const qtTargets = grid1Rows.filter(
        (row: any) => normalizeKey(row?.fg_unit) === "QT" && toNum(row?.qt) > 0
      );

      for (const target of qtTargets) {
        const cdMatl = normalizeKey(target?.cd_matl);
        let need = toNum(target?.qt);

        const fifoRows = inventoryRows.filter(
          (inv: any) =>
            normalizeKey(inv?.fg_unit) === "QT" &&
            normalizeKey(inv?.cd_matl) === cdMatl
        );

        for (const inv of fifoRows) {
          if (need <= 0) break;
          const remain = getRemain(inv);
          if (remain <= 0) continue;
          const take = Math.min(need, remain);
          consumeQty(inv, take);
          need -= take;
          resultRows.push({
            ...inv,
            qt: take,
            qt_w: take,
            qt_b: 0,
          });
        }

        if (need > 0) {
          await showDialog(
            "alter",
            `[${target?.cd_matl || ""}] 재고가 부족합니다. 부족수량: ${need}`
          );
          return false;
        }
      }

      // LOT/SN: grid3(cd_matl+no_barcode) 기준 매칭
      for (const row of grid3Rows) {
        const fgUnit = normalizeKey(row?.fg_unit);
        if (fgUnit !== "LOT" && fgUnit !== "SN") continue;

        const cdMatl = normalizeKey(row?.cd_matl);
        const noBarcode = normalizeKey(row?.no_barcode);
        const need = toNum(row?.qt);
        if (!cdMatl || !noBarcode || need <= 0) continue;

        const inv = inventoryRows.find(
          (invRow: any) =>
            normalizeKey(invRow?.cd_matl) === cdMatl &&
            normalizeKey(invRow?.no_barcode) === noBarcode
        );

        if (!inv) {
          await showDialog(
            "alter",
            `[${row?.cd_matl || ""}] 재고를 찾을 수 없습니다. 바코드: ${row?.no_barcode || ""}`
          );
          return false;
        }

        const remain = getRemain(inv);
        if (remain < need) {
          await showDialog(
            "alter",
            `[${row?.cd_matl || ""}] 재고가 부족합니다. 부족수량: ${need - remain}`
          );
          return false;
        }

        consumeQty(inv, need);
        resultRows.push({
          ...inv,
          qt: need,
          qt_w: need,
          qt_b: 0,
        });
      }

      return resultRows;
    };

    // 재고조회(grid2) 선택행을 처리내역(grid3)으로 추가
    const onMoveStockToProcess = async (id?: string) => {
      if (id !== "addRow") return;
      gridRefs.current.grid2.current?.stopEditing?.();
      const checkedRows = gridRefs.current.grid2.current?.getCheckedRows?.() || [];
      if (!checkedRows.length) {
        await showDialog("alter", "선택된 LOT/SN 정보가 없습니다.");
        return;
      }
      for (const row of checkedRows) {
        await moveGrid2RowToGrid3(
          { ...row, qt_use: row?.qt_use },
          -1,
          "manual"
        );
      }
    };

    const onGrid2RowDoubleClicked = async (event: any) => {
      gridRefs.current.grid2.current?.stopEditing?.();
      const latestUseQty =
        gridRefs.current.grid2.current?.getValue?.("qt_use", event?.node) ??
        event?.data?.qt_use;
      await moveGrid2RowToGrid3(
        { ...event?.data, qt_use: latestUseQty },
        event?.rowIndex ?? -1,
        "manual"
      );
    };

    const onGrid2CellValueChanged = async (event: any) => {
      const colId =
        event?.colDef?.field ||
        event?.colDef?.cd_column ||
        event?.column?.getColId?.() ||
        "";
      if (normalizeKey(colId) !== "QT_USE") return;

      const useQty = toNum(event?.data?.qt_use);
      const remainQty = toNum(event?.data?.qt_remain);
      if (useQty > remainQty) {
        await showDialog("alter", "적용수량은 현재고를 초과할 수 없습니다.");
        event?.node?.setDataValue?.("qt_use", remainQty, "noEvent");
      }
      if (useQty < 0) {
        event?.node?.setDataValue?.("qt_use", 0, "noEvent");
      }
    };

    // 처리내역(grid3) 선택행 삭제
    const onDeleteProcessRows = () => {
      gridRefs.current.grid3.current?.deleteSelectedRows?.();
      recalcGrid2QtyUseRemain();
      void syncGrid3FooterToGrid1SelectedQt();
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {},
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCellValueChanged: onGrid2CellValueChanged,
            // onRowDoubleClicked: onGrid2RowDoubleClicked,
          },
        },
        grid3: {
          columns: columns.grid3,
          dataSource: dataSources.grid3,
          events: {
            onBtnAddClick: onMoveStockToProcess,
            onBtnDeleteClick: onDeleteProcessRows,
            onCellFocused: () => {
              focusStockBarcodeInput();
            },
            onCellValueChanged: () => {
              void syncGrid3FooterToGrid1SelectedQt();
            },
          },
        },
      }),
      [columns, dataSources]
    );

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        const rows = await buildPopupReturnRows();
        if (rows === false) return false;
        popupReturnRowsRef.current = Array.isArray(rows) ? rows : [];
        return true;
      },
      getOkayData: () => {
        return {
          closeFlag: true,
          rtnData: popupReturnRowsRef.current || [],
        };
      },
    }));

    useEffect(() => {
      setPopupParentEl(popupRootRef.current);
    }, []);

    useEffect(() => {
      const init = async () => {
        try {
          const rtn: any = await getInitPopUpData("PRD_MANUAL_CONS_POP_01", {});
          setColumns({
            grid1: rtn?.grid?.grid1 || [],
            grid2: rtn?.grid?.grid2 || [],
            grid3: rtn?.grid?.grid3 || [],
          });

          try {
            const params = {
              id: "sn_prd_manual_cons_pop_01",
              params: {
                cd_firm: userInfo.cd_firm,
                no_wo: selectedData?.no_wo,
                no_line: selectedData?.no_line,
                qt: selectedData?.qt,
              },
            };

            const data: any = await fnGetDataSet(params);
            const inventoryRows = Array.isArray(data?.[1]) ? data[1] : [];
            inventoryRowsRef.current = inventoryRows;

            setDataSources({
              grid1: data?.[0] || [],
              grid2: filterNonQtRows(inventoryRows),
              grid3: data?.[2] || [],
            });

            setTimeout(() => {
              recalcGrid2QtyUseRemain();
            }, 0);

            // grid1 첫 행에 포커스를 맞춰 filter(refs) 초기 적용
            setTimeout(() => {
              gridRefs.current.grid1.current?.selectAndFocusRow?.(0);
            }, 0);
          } catch {
            setDataSources({ grid1: [], grid2: [], grid3: [] });
          }
        } catch (error: any) {
          showDialog(
            "error",
            error?.message || "팝업 초기화 중 오류가 발생했습니다."
          );
        }
      };
      init();
    }, [Menuprops, selectedData]);

    return (
      <div
        className="h-full w-full flex flex-col min-h-0 overflow-hidden"
        ref={popupRootRef}
      >
        <div className="h-full w-full min-h-0 flex flex-col gap-2 p-1 overflow-hidden">
          <SnResizable
            direction={"height"}
            seq={1}
            cd_menu={resizeMenuKey}
            defaultSize={defaultSize}
            size={sizes.tab1Grid}
            onResizeStop={onResizeStop}
            enable={{ bottom: true }}
            minHeight={100}
          >
            <SnGrid
              ref={gridRefs.current.grid1}
              popupParent={popupParentEl || undefined}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{ rowSelection: "single", title: "투입자재" }}
              events={grids.grid1.events}
              filter={{
                refs: [gridRefs.current.grid2, gridRefs.current.grid3],
                columns: ["cd_matl"],
              }}
            />
          </SnResizable>

          <SnResizable
            direction={"height"}
            seq={2}
            cd_menu={resizeMenuKey}
            defaultSize={defaultSize}
            size={sizes.tab2Grid}
            onResizeStop={onResizeStop}
            enable={{ bottom: true }}
            minHeight={100}
          >
            <SnGrid
              ref={gridRefs.current.grid2}
              popupParent={popupParentEl || undefined}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{
                rowSelection: "multiple",
                ischeckboxSelection: true,
                title: "재고조회",
              }}
              header={
                <div className="w-full flex items-center justify-start">
                  <div className="w-60">
                    <SnBarcodeBox
                      inputRef={stockBarcodeRef}
                      onEnter={onStockBarcodeEnter}
                    />
                  </div>
                </div>
              }
              events={grids.grid2.events}
            />
          </SnResizable>

          <SnResizable
            direction={"height"}
            seq={3}
            cd_menu={resizeMenuKey}
            defaultSize={defaultSize}
            size={sizes.tab3Grid}
            onResizeStop={onResizeStop}
            enable={{ bottom: true }}
            minHeight={100}
          >
            <SnGrid
              ref={gridRefs.current.grid3}
              popupParent={popupParentEl || undefined}
              columnConfig={grids.grid3.columns}
              rowData={grids.grid3.dataSource}
              etcProps={{
                rowSelection: "single",
                isEditable: true,
                title: "처리내역",
              }}
              events={grids.grid3.events}
            />
          </SnResizable>
        </div>
      </div>
    );
  }
);

export default PRD_MANUAL_CONS_POP_01;
